import type { ImportantDateFormValues, ImportantDatePayload, ImportantDateWithOccurrence, Person, PersonFormValues, PersonPayload } from '../../api/types';
import { splitTags } from '../../utils/format';

export const defaultPersonFormValues: PersonFormValues = {
  targetType: 'person',
  name: '',
  relation: '',
  category: '',
  memo: '',
  tags: '',
};

export const toPersonFormValues = (person: Person): PersonFormValues => ({
  targetType: person.targetType,
  name: person.name,
  relation: person.relation ?? '',
  category: person.category ?? '',
  memo: person.memo ?? '',
  tags: person.tags.join(', '),
});

export const toPersonPayload = (values: PersonFormValues): PersonPayload => ({
  targetType: values.targetType,
  name: values.name.trim(),
  relation: values.relation.trim() || null,
  category: values.category.trim() || null,
  memo: values.memo.trim() || null,
  tags: splitTags(values.tags),
});

export const defaultDateFormValues: ImportantDateFormValues = {
  type: 'birthday',
  title: '',
  originalYear: '',
  originalMonth: '',
  originalDay: '',
  calendarType: 'solar',
  isLeapMonth: false,
  repeat: 'annual',
  reminderEnabled: false,
  reminderOffsetDays: '0',
  memo: '',
  tags: '',
};

export const toDateFormValues = (date: ImportantDateWithOccurrence): ImportantDateFormValues => ({
  type: date.type,
  title: date.title,
  originalYear: date.originalYear ? String(date.originalYear) : '',
  originalMonth: String(date.originalMonth),
  originalDay: String(date.originalDay),
  calendarType: date.calendarType,
  isLeapMonth: date.isLeapMonth,
  repeat: date.repeat,
  reminderEnabled: date.reminderEnabled,
  reminderOffsetDays: String(date.reminderOffsetDays),
  memo: date.memo ?? '',
  tags: date.tags.join(', '),
});

export const toDatePayload = (values: ImportantDateFormValues): ImportantDatePayload => ({
  type: values.type,
  title: values.title.trim(),
  originalYear: values.originalYear ? Number(values.originalYear) : null,
  originalMonth: Number(values.originalMonth),
  originalDay: Number(values.originalDay),
  calendarType: values.calendarType,
  isLeapMonth: values.calendarType === 'lunar' ? values.isLeapMonth : false,
  repeat: values.repeat,
  reminderEnabled: values.reminderEnabled,
  reminderOffsetDays: Number(values.reminderOffsetDays || 0),
  memo: values.memo.trim() || null,
  tags: splitTags(values.tags),
});

export const validatePersonForm = (values: PersonFormValues) => {
  if (!values.name.trim()) return '이름을 입력해 주세요.';
  if (values.name.trim().length > 60) return '이름은 60자 이하로 입력해 주세요.';
  return null;
};

export const validateDateForm = (values: ImportantDateFormValues) => {
  if (!values.title.trim()) return '날짜 제목을 입력해 주세요.';
  const month = Number(values.originalMonth);
  const day = Number(values.originalDay);
  const year = values.originalYear ? Number(values.originalYear) : null;
  const reminderOffset = Number(values.reminderOffsetDays || 0);
  if (!Number.isInteger(month) || month < 1 || month > 12) return '월은 1부터 12 사이로 입력해 주세요.';
  if (!Number.isInteger(day) || day < 1 || day > 31) return '일은 1부터 31 사이로 입력해 주세요.';
  const probe = new Date(Date.UTC(2024, month - 1, day));
  if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return '유효한 월/일 조합을 입력해 주세요.';
  if ((values.type === 'dday' || values.repeat === 'none') && (!year || !Number.isInteger(year))) {
    return 'D-day 또는 반복 없음 날짜는 연도를 입력해 주세요.';
  }
  if (!Number.isInteger(reminderOffset) || reminderOffset < 0 || reminderOffset > 366) {
    return '알림 기준일은 0부터 366 사이로 입력해 주세요.';
  }
  return null;
};
